/**
 * useRoadmapActions - Task action dispatchers
 * Extracted from hooks.ts for readability
 */

import { useMemo } from 'react';
import { globalTasksStore } from '../../stores/globalTasksStore';
import { useActiveBoardContext } from '../stores/ActiveBoardContext';
import type { GlobalTask } from '../../types';
import type { Task, TaskState } from '../../stores/types';
import { getNextTaskId } from '../../stores/taskIdStore';
import { businessDayIndexToDate, getTaskDayIndex } from '../../lib/dateUtils';
import { getTaskDuration } from '../utils/boardHelpers';

export function useRoadmapActions() {
  const { boardId, boardStartDate, boardTaskViewStore } = useActiveBoardContext();

  return useMemo(() => ({
    moveTask: (taskId: string, column: string, startDate: string) => {
      globalTasksStore.send({ type: 'task.updateStartDate', taskId, startDate });
      boardTaskViewStore.send({ type: 'board.moveTaskColumn', taskId, column });
    },

    updateStartDate: (taskId: string, startDate: string) =>
      globalTasksStore.send({ type: 'task.updateStartDate', taskId, startDate }),

    updateColumn: (taskId: string, column: string) =>
      boardTaskViewStore.send({ type: 'board.moveTaskColumn', taskId, column }),

    addAssignee: (taskId: string, employeeId: string) =>
      globalTasksStore.send({ type: 'task.addAssignee', taskId, employeeId }),

    removeAssignee: (taskId: string, employeeId: string) =>
      globalTasksStore.send({ type: 'task.removeAssignee', taskId, employeeId }),

    clearAssignees: (taskId: string) =>
      globalTasksStore.send({ type: 'task.clearAssignees', taskId }),

    removeFromBoard: (taskId: string) =>
      boardTaskViewStore.send({ type: 'board.removeTask', taskId }),

    addToBoard: (taskId: string, column: string) =>
      boardTaskViewStore.send({ type: 'board.addTask', taskId, column }),

    updateCategories: (taskId: string, categoryIds: string[]) =>
      globalTasksStore.send({ type: 'task.updateCategories', taskId, categoryIds }),

    importTasks: (taskStates: TaskState[]) => {
      for (const saved of taskStates) {
        const globalTask = globalTasksStore.getSnapshot().context.tasksById[saved.id];
        if (!globalTask) continue;
        globalTasksStore.send({ type: 'task.updateStartDate', taskId: saved.id, startDate: saved.startDate });
      }
    },

    resetTasks: (tasks: Task[]) => {
      const globalTasks: GlobalTask[] = tasks.map(t => ({
        id: t.id,
        title: t.title,
        startDate: t.startDate,
        estimateMode: t.estimateMode,
        estimates: t.estimates,
        assigneeIds: t.assigneeIds,
        categoryIds: t.categoryIds ?? [],
        ...(t.description && { description: t.description }),
        ...(t.consequence && { consequence: t.consequence }),
        ...(t.defaultEstimate && { defaultEstimate: t.defaultEstimate }),
        ...(t.requiredSkills && { requiredSkills: t.requiredSkills }),
        ...(t.requirements_hard && { requirements_hard: t.requirements_hard }),
        ...(t.requirements_soft && { requirements_soft: t.requirements_soft }),
      }));

      const views: Record<string, { column: string }> = {};
      for (const t of tasks) {
        views[t.id] = {
          column: t.column,
        };
      }

      globalTasksStore.send({ type: 'tasks.reset', tasks: globalTasks });
      boardTaskViewStore.send({ type: 'board.importViews', views });
    },

    shiftAllDays: (delta: number) =>
      globalTasksStore.send({ type: 'tasks.shiftAllDays', delta, boardStartDate }),

    shiftDays: (delta: number, taskIds?: string[]) =>
      globalTasksStore.send({ type: 'tasks.shiftDays', delta, taskIds, boardStartDate }),

    addTask: (task: Task) => {
      const globalTask: GlobalTask = {
        id: task.id,
        title: task.title,
        startDate: task.startDate,
        estimateMode: task.estimateMode,
        estimates: task.estimates,
        assigneeIds: task.assigneeIds,
        categoryIds: task.categoryIds ?? [],
        ...(task.description && { description: task.description }),
        ...(task.consequence && { consequence: task.consequence }),
        ...(task.defaultEstimate && { defaultEstimate: task.defaultEstimate }),
        ...(task.requiredSkills && { requiredSkills: task.requiredSkills }),
        ...(task.requirements_hard && { requirements_hard: task.requirements_hard }),
        ...(task.requirements_soft && { requirements_soft: task.requirements_soft }),
      };
      globalTasksStore.send({ type: 'task.add', task: globalTask });
      boardTaskViewStore.send({ type: 'board.addTask', taskId: task.id, column: task.column });
    },

    deleteTask: (taskId: string) =>
      globalTasksStore.send({ type: 'task.delete', taskId }),

    resizeTask: (taskId: string, durationDays: number, startDayDelta?: number) =>
      globalTasksStore.send({ type: 'task.resize', taskId, durationDays, startDayDelta, boardStartDate }),

    duplicateTask: (taskId: string) => {
      const globalTask = globalTasksStore.getSnapshot().context.tasksById[taskId];
      if (!globalTask) return;
      const newId = getNextTaskId();
      const newGlobalTask: GlobalTask = {
        ...globalTask,
        id: newId,
        title: globalTask.title + ' (Kopie)',
        assigneeIds: [],
      };
      globalTasksStore.send({ type: 'task.add', task: newGlobalTask });

      const views = boardTaskViewStore.getSnapshot().context.views;
      const originalView = views[taskId];
      if (originalView) {
        boardTaskViewStore.send({ type: 'board.addTask', taskId: newId, column: originalView.column });
      }
    },

    splitTask: (taskId: string, dayOffset: number) => {
      const globalTask = globalTasksStore.getSnapshot().context.tasksById[taskId];
      if (!globalTask) return;

      const views = boardTaskViewStore.getSnapshot().context.views;
      const view = views[taskId];
      if (!view) return;

      const materializedTask: Task = { ...globalTask, column: view.column };
      const totalDuration = getTaskDuration(materializedTask);
      if (dayOffset < 1 || dayOffset >= totalDuration) return;

      const ratio = dayOffset / totalDuration;

      const splitEstimate = (est: { o: number; m: number; p: number }, r: number) => ({
        o: Math.max(1, Math.round(est.o * r)),
        m: Math.max(1, Math.round(est.m * r)),
        p: Math.max(1, Math.round(est.p * r)),
      });

      if (globalTask.estimateMode === 'default' && globalTask.defaultEstimate) {
        globalTasksStore.send({
          type: 'task.updateEstimates',
          taskId,
          defaultEstimate: splitEstimate(globalTask.defaultEstimate, ratio),
        });
      } else if (globalTask.estimateMode === 'individual') {
        const newEstimates: Record<string, { o: number; m: number; p: number } | null> = {};
        for (const [empId, est] of Object.entries(globalTask.estimates)) {
          newEstimates[empId] = est ? splitEstimate(est, ratio) : null;
        }
        globalTasksStore.send({ type: 'task.updateEstimates', taskId, estimates: newEstimates });
      }

      const splitDate = businessDayIndexToDate(getTaskDayIndex(globalTask.startDate, boardStartDate) + dayOffset, boardStartDate);
      const newId = getNextTaskId();
      const newGlobalTask: GlobalTask = {
        ...globalTask,
        id: newId,
        title: globalTask.title + ' (Teil 2)',
        startDate: splitDate,
        assigneeIds: [...globalTask.assigneeIds],
      };
      if (globalTask.estimateMode === 'default' && globalTask.defaultEstimate) {
        newGlobalTask.defaultEstimate = splitEstimate(globalTask.defaultEstimate, 1 - ratio);
      }
      if (globalTask.estimateMode === 'individual') {
        const newEstimates: Record<string, { o: number; m: number; p: number } | null> = {};
        for (const [empId, est] of Object.entries(globalTask.estimates)) {
          newEstimates[empId] = est ? splitEstimate(est, 1 - ratio) : null;
        }
        newGlobalTask.estimates = newEstimates;
      }

      globalTasksStore.send({ type: 'task.add', task: newGlobalTask });
      boardTaskViewStore.send({ type: 'board.addTask', taskId: newId, column: view.column });
    },

    updateEstimates: (taskId: string, updates: {
      defaultEstimate?: { o: number; m: number; p: number };
      estimates?: Record<string, { o: number; m: number; p: number } | null>;
    }) => {
      globalTasksStore.send({ type: 'task.updateEstimates', taskId, ...updates });
    },
  }), [boardId, boardStartDate, boardTaskViewStore]);
}
